import { useEffect, useState } from "react";
import { API, api, formatApiError } from "@/lib/api";
import { Loader2, Mail, MessageSquare, Send, ShieldCheck, Smartphone } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function NotificationsTab({ user }) {
  const canTest = user?.role === "super_admin" || user?.role === "admin" || (user?.permissions || []).includes("notifications.send_test");
  const isSuper = user?.role === "super_admin" || user?.role === "admin";
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("Test message from CTP Kenya — your print order is ready for pickup.");
  const [sending, setSending] = useState(false);
  const [toggles, setToggles] = useState(null);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [notifyToggles, setNotifyToggles] = useState(null);
  const [notifyMeta, setNotifyMeta] = useState({ whatsapp_enabled: false, sms_enabled: false });
  const [opsNumber, setOpsNumber] = useState("");
  const [opsNumberSaved, setOpsNumberSaved] = useState("");
  const [opsSaving, setOpsSaving] = useState(false);
  const [opsTesting, setOpsTesting] = useState(false);
  const [togglesBusy, setTogglesBusy] = useState(null); // key currently persisting

  const load = async () => {
    const reqs = [api.get("/notifications/config"), api.get("/admin/notifications")];
    if (isSuper) {
      reqs.push(api.get("/admin/integrations/email-toggles"));
      reqs.push(api.get("/admin/integrations/notify-toggles"));
    }
    const [cfg, ns, tg, nt] = await Promise.all(reqs);
    setConfig(cfg.data);
    setLogs(ns.data);
    if (tg) { setToggles(tg.data.toggles); setResendEnabled(!!tg.data.resend_enabled); }
    if (nt) {
      setNotifyToggles(nt.data.toggles);
      setNotifyMeta({ whatsapp_enabled: nt.data.whatsapp_enabled, sms_enabled: nt.data.sms_enabled });
      setOpsNumber(nt.data.operations_whatsapp_number || "");
      setOpsNumberSaved(nt.data.operations_whatsapp_number || "");
    }
  };
  useEffect(() => { load(); }, []);

  const saveOpsNumber = async () => {
    setOpsSaving(true);
    try {
      const { data } = await api.put("/admin/integrations/notify-toggles", {
        operations_whatsapp_number: opsNumber.trim(),
      });
      const saved = data.operations_whatsapp_number || "";
      setOpsNumberSaved(saved);
      toast.success(saved ? `Ops WhatsApp saved · ${saved}` : "Ops WhatsApp cleared");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not save ops number");
    } finally {
      setOpsSaving(false);
    }
  };

  const testOpsPing = async () => {
    const target = opsNumber.trim();
    if (!target) {
      toast.error("Enter an ops number first (typed or saved).");
      return;
    }
    setOpsTesting(true);
    try {
      const { data } = await api.post("/admin/integrations/notify-toggles/test-ops", { to: target });
      if (data.ok) {
        toast.success(data.message || `Test ping sent to ${data.to}`);
      } else {
        toast.warning(data.message || "WhatsApp responded but did not confirm send.");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Test ping failed");
    } finally {
      setOpsTesting(false);
    }
  };

  const flipToggle = async (key, value) => {
    setTogglesBusy(key);
    try {
      const { data } = await api.put("/admin/integrations/email-toggles", { [key]: value });
      setToggles(data.toggles);
      toast.success(`${key.replace("email_on_", "").replace("_", " ")} email · ${value ? "ON" : "OFF"}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not save toggle");
    } finally {
      setTogglesBusy(null);
    }
  };

  const flipNotifyToggle = async (key, value) => {
    setTogglesBusy(key);
    try {
      const { data } = await api.put("/admin/integrations/notify-toggles", { [key]: value });
      setNotifyToggles(data.toggles);
      const [chan, , event] = key.split("_");
      const label = chan === "wa" ? "WhatsApp" : "SMS";
      toast.success(`${label} · ${event} · ${value ? "ON" : "OFF"}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not save toggle");
    } finally {
      setTogglesBusy(null);
    }
  };

  const sendTest = async () => {
    if (!phone) { toast.error("Enter a phone number"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/notifications/test", { phone, message });
      const smsLabel = data.sms?.status === "sent" ? "SMS sent" : data.sms?.status === "placeholder" ? "SMS (placeholder)" : `SMS ${data.sms?.status}`;
      const waLabel = data.whatsapp?.status === "sent" ? "WhatsApp sent" : data.whatsapp?.status === "placeholder" ? "WhatsApp (placeholder)" : `WhatsApp ${data.whatsapp?.status}`;
      toast.success(`${smsLabel} · ${waLabel}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Test failed");
    }
    setSending(false);
  };

  if (!config) return <Loader2 className="w-6 h-6 animate-spin text-[#059669]" />;

  // eslint-disable-next-line react/no-unstable-nested-components
  const Card = ({ icon: Icon, title, enabled, lines }) => (
    <div className="bg-white border border-[#E5E5E5] p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 border-2 ${enabled ? "border-[#059669] bg-[#059669] text-white" : "border-[#E5E5E5] text-[#525252]"} flex items-center justify-center`}>
            <Icon className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-lg">{title}</div>
            <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${enabled ? "text-[#059669]" : "text-[#F59E0B]"}`}>
              {enabled ? "Active" : "Placeholder Mode"}
            </div>
          </div>
        </div>
      </div>
      <ul className="text-xs text-[#525252] mono space-y-1">
        {lines.map((l, i) => <li key={`${i}-${l.slice(0, 32)}`}>{l}</li>)}
      </ul>
    </div>
  );

  return (
    <div className="space-y-6">
      {isSuper && toggles && (
        <div className="bg-white border-2 border-[#0A0A0A] p-5" data-testid="email-toggles-panel">
          <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ Customer emails</div>
              <h3 className="font-display font-black text-xl mt-1">Order-lifecycle emails</h3>
              <p className="text-xs text-[#525252] mt-1">
                Flip these to stop specific customer-facing emails without touching WhatsApp/SMS. Turn off if a template is being reviewed or your inbox is getting complaints.
              </p>
            </div>
            {!resendEnabled && (
              <span className="px-2 py-1 border border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E] text-[9px] uppercase tracking-[0.2em] font-bold self-start">
                Resend disabled
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { key: "email_on_confirmation", label: "Order confirmation", sub: "Sent right after M-Pesa/wallet payment." },
              { key: "email_on_ready", label: "Order ready", sub: "Sent when status flips to ready." },
              { key: "email_on_completed", label: "Order dispatched", sub: "Sent when status flips to completed." },
            ].map((row) => (
              <label
                key={row.key}
                className="flex items-start justify-between gap-3 border border-[#E5E5E5] hover:border-[#0A0A0A] p-3 cursor-pointer"
                data-testid={`email-toggle-row-${row.key}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-bold flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" /> {row.label}
                  </div>
                  <div className="text-[10px] text-[#525252] mt-1">{row.sub}</div>
                </div>
                <Switch
                  checked={!!toggles[row.key]}
                  disabled={togglesBusy === row.key || !resendEnabled}
                  onCheckedChange={(v) => flipToggle(row.key, v)}
                  data-testid={`email-toggle-${row.key}`}
                  aria-label={`Toggle ${row.label} emails`}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      {isSuper && notifyToggles && (
        <div className="bg-white border-2 border-[#0A0A0A] p-5" data-testid="ops-whatsapp-panel">
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ Internal ops alerts</div>
            <h3 className="font-display font-black text-xl mt-1">Operations WhatsApp number</h3>
            <p className="text-xs text-[#525252] mt-1">
              Sends a WhatsApp ping (reusing the approved <code className="mono text-[10px] bg-[#F5F5F5] px-1">order_confirmation</code> template) to this number the moment a new order becomes paid. Ops person receives the customer&apos;s name, tracking number, and order total — enough to grab the right stack of paper without opening the app. Leave blank to disable.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="tel"
              value={opsNumber}
              onChange={(e) => setOpsNumber(e.target.value)}
              placeholder="07XXXXXXXX or +254712345678"
              data-testid="ops-whatsapp-input"
              className="flex-1 min-w-[220px] px-3 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono text-sm"
            />
            <button
              onClick={saveOpsNumber}
              disabled={opsSaving || opsNumber.trim() === opsNumberSaved.trim()}
              data-testid="ops-whatsapp-save"
              className="px-5 py-3 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#059669] transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {opsSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
              Save
            </button>
            <button
              onClick={testOpsPing}
              disabled={opsTesting || !opsNumber.trim()}
              data-testid="ops-whatsapp-test"
              title="Fire a dummy order_confirmation ping to verify Meta accepts this number."
              className="px-5 py-3 border-2 border-[#0A0A0A] text-[#0A0A0A] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {opsTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
              Send test ping
            </button>
          </div>
          {opsNumberSaved && (
            <div className="mt-3 text-[11px] mono text-[#059669]" data-testid="ops-whatsapp-current">
              Currently active → <b>{opsNumberSaved}</b> · will receive ping on the next paid order.
            </div>
          )}
        </div>
      )}

      {isSuper && notifyToggles && (
        <div className="bg-white border-2 border-[#0A0A0A] p-5" data-testid="notify-toggles-panel">
          <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ Customer WhatsApp &amp; SMS</div>
              <h3 className="font-display font-black text-xl mt-1">Order-lifecycle notifications</h3>
              <p className="text-xs text-[#525252] mt-1">
                Per-channel, per-event control over the Meta WhatsApp Cloud API templates and Africa&apos;s Talking SMS sends. Flip any switch to pause that specific notification without a redeploy.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap self-start">
              {!notifyMeta.whatsapp_enabled && (
                <span className="px-2 py-1 border border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E] text-[9px] uppercase tracking-[0.2em] font-bold">
                  WhatsApp disabled
                </span>
              )}
              {!notifyMeta.sms_enabled && (
                <span className="px-2 py-1 border border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E] text-[9px] uppercase tracking-[0.2em] font-bold">
                  SMS placeholder
                </span>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="notify-toggles-grid">
              <thead>
                <tr className="border-b-2 border-[#0A0A0A]">
                  <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px] font-bold text-[#525252]">Event</th>
                  <th className="text-center p-3 uppercase tracking-[0.15em] text-[10px] font-bold text-[#525252]">WhatsApp</th>
                  <th className="text-center p-3 uppercase tracking-[0.15em] text-[10px] font-bold text-[#525252]">SMS</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { event: "confirmation", label: "Order confirmed", sub: "Fires after M-Pesa/wallet payment succeeds." },
                  { event: "ready", label: "Order ready", sub: "Fires when status flips to ready." },
                  { event: "dispatched", label: "Order dispatched", sub: "Fires when status flips to completed." },
                ].map((row) => {
                  const waKey = `wa_on_${row.event}`;
                  const smsKey = `sms_on_${row.event}`;
                  return (
                    <tr key={row.event} className="border-b border-[#E5E5E5] last:border-b-0">
                      <td className="p-3">
                        <div className="font-bold text-sm">{row.label}</div>
                        <div className="text-[10px] text-[#525252] mt-0.5">{row.sub}</div>
                      </td>
                      <td className="p-3 text-center">
                        <Switch
                          checked={!!notifyToggles[waKey]}
                          disabled={togglesBusy === waKey || !notifyMeta.whatsapp_enabled}
                          onCheckedChange={(v) => flipNotifyToggle(waKey, v)}
                          data-testid={`notify-toggle-${waKey}`}
                          aria-label={`Toggle WhatsApp on ${row.event}`}
                        />
                      </td>
                      <td className="p-3 text-center">
                        <Switch
                          checked={!!notifyToggles[smsKey]}
                          disabled={togglesBusy === smsKey || !notifyMeta.sms_enabled}
                          onCheckedChange={(v) => flipNotifyToggle(smsKey, v)}
                          data-testid={`notify-toggle-${smsKey}`}
                          aria-label={`Toggle SMS on ${row.event}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <Card
          icon={MessageSquare}
          title="SMS · Africa's Talking"
          enabled={config.sms.enabled}
          lines={[
            `provider: ${config.sms.provider}`,
            `username: ${config.sms.username}`,
            `sender_id: ${config.sms.sender_id || "(default)"}`,
          ]}
        />
        <Card
          icon={Smartphone}
          title="WhatsApp · Meta Cloud API"
          enabled={config.whatsapp.enabled}
          lines={[
            `provider: ${config.whatsapp.provider}`,
            `phone_number_id: ${config.whatsapp.phone_number_id || "(not set)"}`,
            `templates: ${Object.values(config.whatsapp.templates || {}).join(", ") || "—"}`,
          ]}
        />
      </div>

      {(!config.sms.enabled || !config.whatsapp.enabled) && (
        <div className="border-2 border-[#F59E0B] bg-[#F59E0B]/5 p-4 text-sm">
          <strong className="uppercase tracking-[0.15em] text-xs">Placeholder mode</strong> · Add credentials to activate real sends:
          <ul className="mt-2 mono text-xs space-y-0.5 text-[#525252]">
            {!config.sms.enabled && <li>• AT_API_KEY (Africa&apos;s Talking dashboard → Settings → API Key)</li>}
            {!config.whatsapp.enabled && <li>• WHATSAPP_ACCESS_TOKEN + WHATSAPP_PHONE_NUMBER_ID (Meta Business Manager)</li>}
          </ul>
        </div>
      )}

      <div className="bg-white border border-[#E5E5E5] p-5" style={{ display: canTest ? "block" : "none" }}>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-4">§ Send a test message</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="07XXXXXXXX"
            data-testid="notify-test-phone"
            className="px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
          />
          <input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Message"
            data-testid="notify-test-message"
            className="px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none sm:col-span-2"
          />
        </div>
        <button
          onClick={sendTest}
          disabled={sending}
          data-testid="notify-send-test"
          className="mt-4 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#059669] disabled:opacity-50"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
        </button>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-3">§ Recent notifications (latest 50)</div>
        <div className="border border-[#E5E5E5] bg-white overflow-x-auto" data-testid="notifications-log">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Sent</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Order</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">To</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Trigger</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">SMS</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-[#525252]">No notifications yet.</td></tr>
              )}
              {logs.map((n) => (
                <tr key={n.id} className="border-b border-[#E5E5E5] last:border-b-0">
                  <td className="p-3 text-[#525252] text-xs">{new Date(n.created_at).toLocaleString()}</td>
                  <td className="p-3 mono font-bold">{n.tracking_number || "—"}</td>
                  <td className="p-3 mono">{n.to}</td>
                  <td className="p-3 uppercase tracking-[0.15em] text-[10px] font-bold">{n.trigger}</td>
                  <td className="p-3"><Badge status={n.sms?.status} /></td>
                  <td className="p-3"><Badge status={n.whatsapp?.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Badge({ status }) {
  const map = {
    sent: "border-[#059669] text-[#059669]",
    placeholder: "border-[#F59E0B] text-[#F59E0B]",
    skipped: "border-[#525252] text-[#525252]",
    failed: "border-[#DC2626] text-[#DC2626]",
  };
  const cls = map[status] || "border-[#525252] text-[#525252]";
  return <span className={`inline-block px-2 py-0.5 border text-[10px] uppercase tracking-[0.15em] font-bold ${cls}`}>{status || "—"}</span>;
}
